/**
 * Annulation depuis la fiche (SPEC FC13, maquette 12-modale-annulation). Même requête et mêmes
 * messages que `screens/Class/components/CancelVisitModal.tsx` (v1, utilisé par Mon compte) :
 * `POST class/cancel { VisitId, ClassId, StartDateTime, SiteId }`, puis visites et forfaits relus,
 * toast « La réservation a été annulée » ; erreur : `resolveUserError('cancelBooking')`.
 * Une réservation à deux = deux visites, annulées chacune par sa propre requête (une visite par
 * requête, comme la v1) : la modale propose d'annuler les deux places ou la seule place de l'invité
 * (lib/cancel.ts), et dit quelle place est annulée si une requête échoue après une autre.
 *
 * La modale est rendue par Chakra dans un portail, hors de #mindbody-app : elle se peint avec les
 * jetons du thème Chakra v2 (`mmv2.*`), pas avec les classes de ux-v2.css. Même coque que les modales
 * de Mon compte (AccountModal : croix ronde, boutons pleine largeur ; ModalButton « danger ») : le
 * client voit la même modale d'annulation qu'il annule depuis la fiche ou depuis Mon compte (revue du
 * 04/10).
 */
import { Box, chakra, Flex, Text, useToast } from '@chakra-ui/react';
import { useListClientServices, useListVisits } from 'hooks/useApiQueries';
import { useEffect, useState } from 'react';
import { isVisitLateCancel } from 'utils/functions';
import { HTTP } from 'utils/http';
import { resolveUserError } from 'utils/userErrorMessages';
import {
  cancelSuccessTitle,
  partialCancelMessage,
  seatsToCancel,
  spotsText,
  type CancelScope,
  type CancelSeat,
} from '../lib/cancel';
import { lateCancelWarning } from '../lib/format';
import { AccountModal, ModalButton, ModalNotice } from '../../Account/components/Ui';

export interface CancelModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Places réservées par le client sur ce cours, la sienne d'abord (lib/cancel.ts cancelSeats). */
  seats: CancelSeat[];
  summary: string;
  lateCancelHours: number;
  onCancelled: () => void;
}

export function CancelModal({ isOpen, onClose, seats, summary, lateCancelHours, onCancelled }: CancelModalProps) {
  const toast = useToast();
  const { refetch: refetchVisits } = useListVisits() || {};
  const { refetch: refetchClientServices } = useListClientServices();
  const [isLoading, setIsLoading] = useState(false);
  const [scope, setScope] = useState<CancelScope>('all');

  const guests = seats.filter((s) => s.isGuest);
  const member = seats.find((s) => !s.isGuest);
  const canChoose = guests.length > 0 && !!member;
  useEffect(() => {
    if (isOpen) setScope('all');
  }, [isOpen]);

  const selected = seatsToCancel(seats, canChoose ? scope : 'all');
  const isLate = selected.some((seat) => isVisitLateCancel(seat.visit));
  const two = selected.length > 1;
  const warning = lateCancelWarning(lateCancelHours, two);

  async function onConfirm() {
    setIsLoading(true);
    const done: CancelSeat[] = [];
    try {
      // = CancelVisitModal.onCancelConfirmation(), une requête par place (invités d'abord).
      for (const seat of selected) {
        const { visit } = seat;
        await HTTP.post(`/class/cancel`, {
          VisitId: visit.Id,
          ClassId: visit.ClassId,
          StartDateTime: visit.StartDateTime,
          SiteId: visit.SiteId,
        }).send();
        done.push(seat);
      }
      onCancelled();
      await Promise.all([refetchVisits(), refetchClientServices()]);
      toast({ title: cancelSuccessTitle(done, seats), status: 'success' });
      onClose();
    } catch (error) {
      // Échec après une annulation réussie : dire quelle place est annulée et laquelle ne l'est pas.
      const partial = partialCancelMessage(done, selected.slice(done.length));
      const { title, description } = partial ?? resolveUserError('cancelBooking', error);
      toast({ title, description, status: 'error', isClosable: true, duration: partial ? null : undefined });
      await Promise.all([refetchVisits(), refetchClientServices()]);
      onCancelled();
      if (partial) onClose();
    } finally {
      setIsLoading(false);
    }
  }

  const options: Array<{ value: CancelScope; title: string; detail: string }> = canChoose
    ? [
        {
          value: 'all',
          title: guests.length > 1 ? 'Toutes mes places' : 'Ma place et celle de mon invité',
          detail: spotsText(seats),
        },
        {
          value: 'guest',
          title: guests.length > 1 ? 'Seulement les places de mes invités' : 'Seulement la place de mon invité',
          detail: [spotsText(guests), `vous gardez votre place${member?.spotNumber ? ` (#${member.spotNumber})` : ''}`]
            .filter(Boolean)
            .join('\u00a0; '),
        },
      ]
    : [];

  return (
    <AccountModal
      isOpen={isOpen}
      onClose={onClose}
      className="mmv2-cancel-modal"
      title="Annuler ma réservation"
      footer={
        <>
          <ModalButton tone="quiet" onClick={onClose} isDisabled={isLoading}>
            Garder ma réservation
          </ModalButton>
          <ModalButton tone="danger" onClick={onConfirm} isLoading={isLoading} loadingText="Annulation…">
            {isLate ? 'Annuler quand même' : 'Confirmer l’annulation'}
          </ModalButton>
        </>
      }
    >
      <Text fontSize="15.5px" lineHeight="1.6" color="mmv2.ink2">
        {summary}
      </Text>
      {canChoose ? (
        <Box as="fieldset" mt="18px" mb="0" mx="0" border="0" p="0" minW="0" className="mmv2-cancel-scope">
          <Box as="legend" fontSize="13.5px" fontWeight={600} color="mmv2.ink" mb="10px" p="0">
            Que souhaitez-vous annuler&nbsp;?
          </Box>
          <Flex direction="column" gap="8px">
            {options.map((option) => {
              const checked = scope === option.value;
              return (
                <Flex
                  key={option.value}
                  as="label"
                  align="flex-start"
                  gap="12px"
                  p="12px 16px"
                  borderRadius="mmv2Card"
                  borderWidth="1.5px"
                  borderColor={checked ? 'mmv2.ink' : 'mmv2.line'}
                  bg={checked ? 'mmv2.sunken' : 'mmv2.paper'}
                  cursor={isLoading ? 'not-allowed' : 'pointer'}
                  data-selected={checked ? 'true' : undefined}
                  sx={{
                    '&:has(input:focus-visible)': {
                      outline: '2.5px solid',
                      outlineColor: 'mmv2.focus',
                      outlineOffset: '2px',
                    },
                  }}
                >
                  <chakra.input
                    type="radio"
                    name="mmv2-cancel-scope"
                    value={option.value}
                    checked={checked}
                    disabled={isLoading}
                    onChange={() => setScope(option.value)}
                    // Centrée sur la 1re ligne du titre (interligne de 20 px) : 1,7 à 1,9 px trop basse avec 3 px.
                    mt="2px"
                    w="16px"
                    h="16px"
                    flex="0 0 auto"
                    // Contour de focus porté par la carte de l'option (:has ci-dessus) : pas un second cadre autour du
                    // bouton radio (règle :focus-visible du thème, la modale étant hors de #mindbody-app ; 05/10).
                    sx={{ accentColor: 'var(--chakra-colors-mmv2-ink)', '&:focus-visible': { outline: 'none' } }}
                  />
                  <Box>
                    <Text
                      as="span"
                      display="block"
                      fontSize="14.5px"
                      fontWeight={600}
                      color="mmv2.ink"
                      lineHeight="20px"
                    >
                      {option.title}
                    </Text>
                    {option.detail ? (
                      <Text as="span" display="block" fontSize="13px" color="mmv2.ink2" lineHeight="1.5">
                        {option.detail}
                      </Text>
                    ) : null}
                  </Box>
                </Flex>
              );
            })}
          </Flex>
        </Box>
      ) : null}
      <Box mt="16px" role={isLate ? 'alert' : undefined}>
        <ModalNotice tone={isLate ? 'warm' : 'info'}>
          {isLate ? (
            <>
              <b>{warning.lead}</b> {warning.text}
            </>
          ) : (
            <>{two ? 'Les crédits des deux places repartent' : 'Le crédit repart'} directement dans votre compte.</>
          )}
        </ModalNotice>
      </Box>
    </AccountModal>
  );
}
